import {shape, string} from 'prop-types';
import {test} from 'ramda';
import {pipeAllEndo} from '@sinclair-digital/sbg-util/dist/es/function';
import {modify} from 'partial.lenses';
import {
    validatorShape,
    hasErrors as hasValidationErrors,
} from '@sinclair-digital/sbg-storyline-components/models';
import {Validator, toValidatedField} from '@sinclair-digital/stl-common/src/ValidatedField';
import {PropertySiteV1} from '@sinclair-digital/stl-common/src/models/Property';

export interface ThemeSettings {
    backgroundColorOne: Validator<string>;
    backgroundColorTwo: Validator<string>;
    brandColorOne: Validator<string>;
    brandColorTwo: Validator<string>;
    faviconImageUrl: string;
    footerLogoImageUrl: string;
    galleryLogoImageUrl: string;
    headerLogoImageUrl: string;
    textColorFour: Validator<string>;
    textColorOne: Validator<string>;
    textColorThree: Validator<string>;
    textColorTwo: Validator<string>;
    weatherImageOneUrl: string;
    weatherImageTwoUrl: string;
}

export const themeOptics = {
    backgroundColorOne: 'backgroundColorOne',
    backgroundColorTwo: 'backgroundColorTwo',
    brandColorOne: 'brandColorOne',
    brandColorTwo: 'brandColorTwo',
    faviconImageUrl: 'faviconImageUrl',
    footerLogoImageUrl: 'footerLogoImageUrl',
    galleryLogoImageUrl: 'galleryLogoImageUrl',
    headerLogoImageUrl: 'headerLogoImageUrl',
    textColorFour: 'textColorFour',
    textColorOne: 'textColorOne',
    textColorThree: 'textColorThree',
    textColorTwo: 'textColorTwo',
};

export const themeSettingsShape = {
    backgroundColorOne: shape(validatorShape(string)),
    backgroundColorTwo: shape(validatorShape(string)),
    brandColorOne: shape(validatorShape(string)),
    brandColorTwo: shape(validatorShape(string)),
    faviconImageUrl: string,
    footerLogoImageUrl: string,
    galleryLogoImageUrl: string,
    headerLogoImageUrl: string,
    textColorFour: shape(validatorShape(string)),
    textColorOne: shape(validatorShape(string)),
    textColorThree: shape(validatorShape(string)),
    textColorTwo: shape(validatorShape(string)),
    weatherImageOneUrl: string,
    weatherImageTwoUrl: string,
};

export const defaultThemeSettings: ThemeSettings = {
    headerLogoImageUrl: '/resources/assets/common/kutv-header-logo.png',
    footerLogoImageUrl: '/resources/assets/common/kutv-footer-logo.png',
    faviconImageUrl: '/resources/assets/common/news-favicon.png',
    brandColorOne: toValidatedField('#56a1d5'),
    textColorOne: toValidatedField('#ffffff'),
    brandColorTwo: toValidatedField('#ffffff'),
    textColorTwo: toValidatedField('#ffffff'),
    backgroundColorOne: toValidatedField('#ffffff'),
    galleryLogoImageUrl: '/resources/assets/common/kutv-gallery-logo.png',
    textColorThree: toValidatedField('#333333'),
    backgroundColorTwo: toValidatedField('#ebebeb'),
    textColorFour: toValidatedField('#56a1d5'),
    // required fields for save but not in form
    weatherImageOneUrl: '/resources/assets/common/kutv-weather-hero-day.jpg',
    weatherImageTwoUrl: '/resources/assets/common/kutv-weather-hero-day.jpg',
};

const requiredColorFields = [
    themeOptics.backgroundColorOne,
    themeOptics.backgroundColorTwo,
    themeOptics.brandColorOne,
    themeOptics.brandColorTwo,
    themeOptics.textColorOne,
    themeOptics.textColorTwo,
    themeOptics.textColorThree,
    themeOptics.textColorFour,
];

// Validates 6 digit hex preceeded by #, for native color picker
// cannot be shorthand hex code i.e #fff
// eslint-disable-next-line mocha/no-global-tests
export const isHexColor = test(/^#([0-9A-F]{6})$/i);

export const validatedFields = requiredColorFields;

export const validateColor = <T>(lens: any) =>
    modify<T>(lens, validatorObj => {
        const isValid = isHexColor(validatorObj.value);
        const errorMessage = isValid ? undefined : 'Invalid hex color';
        return validatorObj.errorMessage === errorMessage
            ? validatorObj
            : {
                ...validatorObj,
                errorMessage,
            };
    });

export const validateColorFields = (lenses: any[]) =>
    pipeAllEndo<ThemeSettings>(lenses.map(lens => validateColor<ThemeSettings>(lens)));

export const validate = validateColorFields(requiredColorFields);

export const hasThemeSettingsErrors: (theme: ThemeSettings) => boolean = hasValidationErrors(validatedFields);

export const parseThemeSettings = ({
    backgroundColorOne,
    backgroundColorTwo,
    brandColorOne,
    brandColorTwo,
    faviconImageUrl,
    footerLogoImageUrl,
    galleryLogoImageUrl,
    headerLogoImageUrl,
    textColorFour,
    textColorOne,
    textColorThree,
    textColorTwo,
    weatherImageOneUrl,
    weatherImageTwoUrl,
}: PropertySiteV1): ThemeSettings => ({
    backgroundColorOne: toValidatedField(backgroundColorOne),
    backgroundColorTwo: toValidatedField(backgroundColorTwo),
    brandColorOne: toValidatedField(brandColorOne),
    brandColorTwo: toValidatedField(brandColorTwo),
    faviconImageUrl,
    footerLogoImageUrl,
    galleryLogoImageUrl,
    headerLogoImageUrl,
    textColorFour: toValidatedField(textColorFour),
    textColorOne: toValidatedField(textColorOne),
    textColorThree: toValidatedField(textColorThree),
    textColorTwo: toValidatedField(textColorTwo),
    weatherImageOneUrl,
    weatherImageTwoUrl,
});

export const serializeThemeSettings = ({
    backgroundColorOne,
    backgroundColorTwo,
    brandColorOne,
    brandColorTwo,
    faviconImageUrl,
    footerLogoImageUrl,
    galleryLogoImageUrl,
    headerLogoImageUrl,
    textColorFour,
    textColorOne,
    textColorThree,
    textColorTwo,
    weatherImageOneUrl,
    weatherImageTwoUrl,
}: ThemeSettings) => ({
    backgroundColorOne: backgroundColorOne.value,
    backgroundColorTwo: backgroundColorTwo.value,
    brandColorOne: brandColorOne.value,
    brandColorTwo: brandColorTwo.value,
    faviconImageUrl,
    footerLogoImageUrl,
    galleryLogoImageUrl,
    headerLogoImageUrl,
    textColorFour: textColorFour.value,
    textColorOne: textColorOne.value,
    textColorThree: textColorThree.value,
    textColorTwo: textColorTwo.value,
    weatherImageOneUrl,
    weatherImageTwoUrl,
});
